import React, { FunctionComponent } from 'react';
import { TicketStatus } from '../../../models/tickets';
import { TicketContentProps } from '../TicketContent';
import { TicketDonationFieldProps } from '../TicketDonationField';
import TicketDonationSwitcher from '../TicketDonationSwitcher';
import type { TicketSalesStatusSummaryProps } from '../TicketSalesStatusSummary';
import TicketSalesStatusSummary from '../TicketSalesStatusSummary';
import type { ConnectedTicketSelectorSwitcherProps } from '../TicketSelectorSwitcher';
import TicketSelectorSwitcher from '../TicketSelectorSwitcher';

export type TicketQuantityPickerProps = Pick<
    ConnectedTicketSelectorSwitcherProps,
    'name' | 'selectedQuantity' | 'isDisabled'
> &
    Pick<
        TicketContentProps,
        | 'currencyFormat'
        | 'currencySymbol'
        | 'donationAmount'
        | 'isCurrencySuffix'
    > &
    TicketSalesStatusSummaryProps & {
        ticketId: string;
        selectableQuantities: Array<number>;
        onQuantityChange: (newQuantity: number) => void;
        onDonationChange: TicketDonationFieldProps['onChange'];
        isDonation: boolean;
    };

const TicketQuantityPicker: FunctionComponent<TicketQuantityPickerProps> = (
    props,
) => {
    // The `TicketSelectorSwitcher` is matching the API of the old `TicketQuantitySelector` so it is forced to define
    // `onQuantityChange` parameter as a string. However, the `onQuantityChange` handler defined in the connector is
    // using a number for quantity, not a string. What happens is that `TicketDisplayCardContent` is wrapping the
    // `onQuantityChange` defined by the connector in a function that parses the string as a number and forwards the
    // number to the function defined by the connector.
    // This happens in https://github.com/eventbrite/eb-ui/blob/b786a5e18d8f4d2c964633315005710b6a6097b2/packages/attendee/checkout/src/components/ticketsSelectionList/TicketDisplayCardContent.jsx#L36-L54.
    // To keep stricter types in as many places as possible, we are converting here the string to a number, so that
    // only `TicketQuantityPicker` exposes a stricter interface even though it uses a component that has a looser
    // interface (`TicketSelectorSwitcher`)
    const onQuantityChange = (quantity: string) =>
        props.onQuantityChange(parseInt(quantity, 10));

    switch (props.ticketStatus) {
        case TicketStatus.onSale:
        case TicketStatus.salesEndSoon:
        case TicketStatus.soldOut:
            return props.isDonation ? (
                <TicketDonationSwitcher
                    id={props.ticketId}
                    isDisabled={props.isDisabled}
                    onChange={props.onDonationChange}
                    amount={props.donationAmount}
                    currencySymbol={props.currencySymbol}
                    currencyFormat={props.currencyFormat}
                    isCurrencySuffix={props.isCurrencySuffix}
                />
            ) : (
                <TicketSelectorSwitcher
                    id={props.ticketId}
                    name={props.name}
                    ticketOptionsRange={props.selectableQuantities}
                    selectedQuantity={props.selectedQuantity}
                    onQuantityChange={onQuantityChange}
                    isDisabled={props.isDisabled}
                    displayPrice={null}
                />
            );
        case TicketStatus.salesEnded:
        case TicketStatus.salesStartAfter:
        case TicketStatus.unavailable:
            return (
                <TicketSalesStatusSummary ticketStatus={props.ticketStatus} />
            );
        case TicketStatus.salesNotStarted:
        case TicketStatus.salesStartSoon:
            return (
                <TicketSalesStatusSummary
                    ticketStatus={props.ticketStatus}
                    formattedSalesStartDate={props.formattedSalesStartDate}
                />
            );
    }
};

export default TicketQuantityPicker;
